import { selectsOrganization } from '@actions/organizations.actions';
import { selectDefaultOrganization } from '@actions/organizations.selectors';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { OrganizationsService } from '@services/organizations.service';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { State } from 'src/app/reducers';

@UntilDestroy()
@Component({
  selector: 'vnm-organization-dropdown',
  templateUrl: './organization-dropdown.component.html',
  styleUrls: ['./organization-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrganizationDropdownComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OrganizationDropdownComponent),
      multi: true
    }
  ]
})
export class OrganizationDropdownComponent implements ControlValueAccessor, OnInit {
  @Input() label = 'organization-dropdown.label';
  @Input() placeholder = 'app.choose-owner';
  @Input() topbar = false; // outside of form scope
  @Input() showClear = false;

  organizationForm = this.fb.group({
    organizationGid: ['', Validators.required]
  });
  @Input() organizations$ = this.organizationsService.list$;

  defaultOrgId$ = this.store.select(selectDefaultOrganization);

  constructor(
    private fb: UntypedFormBuilder,
    private organizationsService: OrganizationsService,
    private store: Store<State>
  ) {}

  ngOnInit() {
    combineLatest([this.organizations$, this.defaultOrgId$])
      .pipe(untilDestroyed(this))
      .subscribe(([organizations, defaultOrganizationId]) => {
        const validOrg = organizations.find((org) => org.gid === defaultOrganizationId);

        if (!validOrg && this.topbar) {
          defaultOrganizationId = organizations[0].gid;
        }
        this.organizationGid = defaultOrganizationId;
      });

    this.organizationForm
      .get('organizationGid')
      .valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((gid) => {
        this.writeValue(gid);
        this.store.dispatch(selectsOrganization({ gid }));
      });
  }

  get organizationGid(): string {
    return this.formControl.value;
  }

  set organizationGid(newOrganizationGid: string) {
    this.writeValue(newOrganizationGid);
  }

  onChange = (organizationGid: string) => {};
  onTouched = () => {};

  writeValue(organizationGid: string) {
    if (!organizationGid) {
      return;
    }
    this.formControl.setValue(organizationGid);
    this.onChange(this.organizationGid);
  }

  registerOnChange(fn: (organizationGid: string) => void) {
    this.onChange = fn;
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe(fn);
    this.onChange(this.organizationGid);
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  validate(_: UntypedFormControl) {
    return this.organizationForm.valid
      ? null
      : { organization: { valid: false, message: 'Please select an Organization' } };
  }

  private get formControl(): AbstractControl {
    return this.organizationForm.get('organizationGid');
  }
}
